import { TETHER_SYMBOL } from "@/constants";
import { formatDateToUTC } from "@/utils/format-date";
import {
  ExchangeInfo,
  KlineData,
  TimeFrame,
  avgPriceSchema,
  exchangeInfoSchema,
  klineRawToKlineDataSchema,
  ticker24FullSchema,
  ticker24MiniSchema,
  tickerFullSchema,
  tickerMiniSchema,
  tickerPrice,
  timeFrameToIntervalSchema,
} from "./schema";

const BASE_URL = "https://api.binance.com";

const Routes = {
  PING: `${BASE_URL}/api/v3/ping`,
  TIME: `${BASE_URL}/api/v3/time`,
  EXCHANGE_INFO: `${BASE_URL}/api/v3/exchangeInfo`,
  DEPTH: `${BASE_URL}/api/v3/depth`,
  TRADES: `${BASE_URL}/api/v3/trades`,
  HISTORICAL_TRADES: `${BASE_URL}/api/v3/historicalTrades`,
  AGGREGATE_TRADES: `${BASE_URL}/api/v3/aggTrades`,
  KLINES: `${BASE_URL}/api/v3/klines`,
  UI_KLINES: `${BASE_URL}/api/v3/uiKlines`,
  AVG_PRICE: `${BASE_URL}/api/v3/avgPrice`,

  TICKER: `${BASE_URL}/api/v3/ticker`,
  TICKER_24HR: `${BASE_URL}/api/v3/ticker/24hr`,
  TICKER_PRICE: `${BASE_URL}/api/v3/ticker/price`,
} as const;

let exchangeInfo: ExchangeInfo | null = null;
const supportedUsdtPairs: Map<string, string> = new Map();

async function getExchangeInfo() {
  if (exchangeInfo) return exchangeInfo;

  const data = await fetch(Routes.EXCHANGE_INFO, { cache: "no-cache" })
    .then((res) => res.json())
    .catch(() => null);

  const parseResult = exchangeInfoSchema.safeParse(data);

  if (parseResult.success && !exchangeInfo) {
    exchangeInfo = parseResult.data;
    exchangeInfo.symbols.forEach((symbol) => {
      if (symbol.quoteAsset === TETHER_SYMBOL) {
        supportedUsdtPairs.set(symbol.baseAsset, symbol.symbol);
      }
    });
  }

  return parseResult.success ? parseResult.data : null;
}

async function getKlines(
  symbol: string,
  interval: string,
  startTime: number,
  limit: number,
  endTime: number = Date.now(),
  baseAsset: string = TETHER_SYMBOL,
): Promise<KlineData[] | null> {
  const params = new URLSearchParams({
    symbol: `${symbol}${baseAsset}`,
    interval,
    startTime: startTime.toString(),
    endTime: endTime.toString(),
    limit: limit.toString(),
  });

  const response = await fetch(`${Routes.KLINES}?${params.toString()}`);
  const data = await response.json();

  const parseResult = klineRawToKlineDataSchema.safeParse(data);
  return parseResult.success ? parseResult.data : [];
}

async function getUIKlines(
  symbol: string,
  interval: string,
  startTime: number,
  limit: number,
  endTime: number = Date.now(),
  baseAsset: string = TETHER_SYMBOL,
) {
  const params = new URLSearchParams({
    symbol: `${symbol}${baseAsset}`,
    interval,
    startTime: startTime.toString(),
    endTime: endTime.toString(),
    limit: limit.toString(),
  });

  const response = await fetch(`${Routes.UI_KLINES}?${params.toString()}`);
  const data = await response.json();

  const parseResult = klineRawToKlineDataSchema.safeParse(data);
  return parseResult.success ? parseResult.data : [];
}

async function getAveragePrice(symbol: string) {
  const response = await fetch(`${Routes.AVG_PRICE}?symbol=${symbol}`);
  const data = await response.json();
  const parseResult = avgPriceSchema.safeParse(data);

  return parseResult.success ? parseResult.data : null;
}

async function getTicker24hr(symbol: string, type: "FULL" | "MINI" = "MINI") {
  const url = `${Routes.TICKER_24HR}?symbol=${symbol}&type=${type}`;
  const response = await fetch(url);
  const data = await response.json();

  if (type === "MINI") {
    const parseResult = ticker24MiniSchema.safeParse(data);
    return parseResult.success ? parseResult.data : null;
  } else {
    const parseResult = ticker24FullSchema.safeParse(data);
    return parseResult.success ? parseResult.data : null;
  }
}

async function getTickerPrice(symbol: string) {
  const url = `${Routes.TICKER_PRICE}?symbol=${symbol}`;
  const response = await fetch(url);
  const data = await response.json();

  const parseResult = tickerPrice.safeParse(data);
  return parseResult.success ? parseResult.data : null;
}

async function getTicker(
  symbol: string,
  windowSize: number,
  type: "FULL" | "MINI" = "MINI",
) {
  const params = new URLSearchParams({
    symbol,
    windowSize: windowSize.toString(),
    type,
  });
  const url = `${Routes.TICKER}?${params.toString()}`;
  const response = await fetch(url);
  const data = await response.json();

  if (type === "MINI") {
    const parseResult = tickerMiniSchema.safeParse(data);
    return parseResult.success ? parseResult.data : null;
  } else {
    const parseResult = tickerFullSchema.safeParse(data);
    return parseResult.success ? parseResult.data : null;
  }
}

async function isAssetSupported(asset: string) {
  // Make sure we have the exchange info
  if (!exchangeInfo) {
    await getExchangeInfo();
  }

  return supportedUsdtPairs.has(asset);
}

type GetAllKlinesDataArgs = {
  symbols: string[];
  interval: TimeFrame;
  startTime: number;
  limit?: number;
  endTime?: number;
};
async function getAllKlinesData({
  symbols,
  interval,
  startTime,
  limit = 1000,
  endTime = Date.now(),
}: GetAllKlinesDataArgs) {
  const uniqueSymbols = Array.from(new Set(symbols));
  const result: Record<string, KlineData[]> = {};

  // Based on the interval it converts the date range into a list of start and end times
  // until the toDate is reached
  const convertToDateRange = (
    fromDate: number,
    toDate: number,
    interval: TimeFrame,
  ) => {
    const dateRange: [number, number][] = [];
    let current = fromDate;

    while (current < toDate) {
      const intervalNumber = timeFrameToIntervalSchema.parse(interval);
      const end = current + limit * intervalNumber;
      dateRange.push([current, end]);
      current = end;
    }

    return dateRange;
  };

  const ranges = convertToDateRange(startTime, endTime, interval);

  for (const symbol of uniqueSymbols) {
    for (const [startTime, endTime] of ranges) {
      const data = await BinanceService.getKlines(
        symbol,
        interval,
        startTime,
        limit,
        endTime,
      );
      if (!data) {
        console.log(
          `[BINANCE_SERVICE::getAllKlinesData] No data for symbol [${symbol}]. Date range ${formatDateToUTC(
            new Date(startTime),
          )} - ${formatDateToUTC(new Date(endTime))}`,
        );
        continue;
      }

      if (!result[symbol]) {
        result[symbol] = data;
      } else {
        result[symbol].push(...data);
      }

      console.log(
        `[BINANCE_SERVICE::getAllKlinesData] Fetched ${
          data.length
        } klines for symbol [${symbol}]. Date range ${formatDateToUTC(
          new Date(startTime),
        )} - ${formatDateToUTC(new Date(endTime))}`,
      );
    }
  }

  return result;
}

export const BinanceService = Object.freeze({
  getAllKlinesData,
  getAveragePrice,
  getExchangeInfo,
  getKlines,
  getTicker,
  getTicker24hr,
  getTickerPrice,
  getUIKlines,
  isAssetSupported,
});

BinanceService.getExchangeInfo();
